<template>
  <div>
    <BaseTableDetail
        v-if="target != null"
        class="order-item-table"
        :table-set-list="tableSetList"
        :display-item="target"
        :editable="editable"
        :site-code="siteCode"
        :validate-scope="validateScope" />
    <el-button
        v-show="showAnbunButton"
        ref="input"
        size="default"
        type="String"
        class="anbun-btn"
        @click="onAnbunSelectClick">
      按分対象選択
    </el-button>
    <BaseDialog
        title="商品検索"
        append-to-body
        width="90%"
        :visible.sync="searchSkusDialogVisible"
        :show-footer="false">
      <template slot="contents">
        <SearchSkus
            :sku-code="target.productId"
            @sku-selected="skuSelected" />
      </template>
    </BaseDialog>
    <!-- 按分対象選択ダイアログ -->
    <AnbunDialog
        :visible.sync="anbunDialogVisible"
        :show-only-close-button="showOnlyCloseButton()"
        :editable="editable"
        :target="target"
        :shipments="shipments" />
  </div>
</template>

<style scoped>
/* TODO ccs-ui.css がきいてしまうので暫定対応。いずれ削除したい */
.order-item-table ::v-deep table tr,
.order-item-table ::v-deep table tr:hover > td {
  background-color: initial;
}
.order-item-table ::v-deep table th {
  border-bottom: initial;
  width: 130px;
}

.anbun-btn {
  margin-left: 15px;
}
</style>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import common from '@/utils/common'
import math from '@/utils/math'
import * as config from '@/config'
import * as define from '@/define'
import * as orderQuery from '@/queries/order'

import orderUtil from '@/components/mixins/orderUtil'

import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import BaseDialog from '@/components/molecules/BaseDialog'

import SearchSkus from '@/components/pages/order/SearchSkus'
import AnbunDialog from '@/components/pages/order/AnbunDialog'

const DISPLAY_TYPE = {
  ITEM: 'item', // 注文明細
  COMPONENT: 'component', // 注文明細（構成品）
  GIFT_ITEM: 'giftItem', // 注文明細（ギフト）
  SHIPPING_ITEM: 'shippingItem', // 注文明細（配送料）
  ADJUSTMENT_ITEM: 'adjustmentItem', // 注文明細（注文調整金額按分）
  ITEM_PRICE_ADJUSTMENT: 'itemPriceAdjustment', // 注文調整金額(明細)
  GIFT_ITEM_PRICE_ADJUSTMENT: 'giftItemPriceAdjustment', // 注文調整金額(ギフト明細)
  SHIPPING_ITEM_PRICE_ADJUSTMENT: 'shippingItemPriceAdjustment', // 注文調整金額(配送明細)
  PRICE_ADJUSTMENT: 'priceAdjustment' // 注文調整金額(注文)
}

const EDITABLE = Object.freeze({
  [DISPLAY_TYPE.ITEM]: Object.freeze({
    brand: true, productId: true, lineitemText: true, colorName: true, sizeName: true,
    isSale: true, isGift: true, basePrice: true, taxRate: true, retailPrice: true, point: true, preparingShipmentStartedAt: true
  }),
  [DISPLAY_TYPE.COMPONENT]: Object.freeze({
    brand: true, productId: true, lineitemText: true, preparingShipmentStartedAt: true
  }),
  [DISPLAY_TYPE.GIFT_ITEM]: Object.freeze({
    price: true
  }),
  [DISPLAY_TYPE.SHIPPING_ITEM]: Object.freeze({
    lineitemText: true, price: true
  }),
  [DISPLAY_TYPE.ADJUSTMENT_ITEM]: Object.freeze({
    price: true
  }),
  [DISPLAY_TYPE.ITEM_PRICE_ADJUSTMENT]: Object.freeze({
    lineitemText: true, price: true, promotionId: true, campaignId: true, couponId: true, reasonCode: true
  }),
  [DISPLAY_TYPE.GIFT_ITEM_PRICE_ADJUSTMENT]: Object.freeze({
    lineitemText: true, price: true, promotionId: true, campaignId: true, couponId: true, reasonCode: true
  }),
  [DISPLAY_TYPE.SHIPPING_ITEM_PRICE_ADJUSTMENT]: Object.freeze({
    lineitemText: true, price: true, promotionId: true, campaignId: true, couponId: true, reasonCode: true
  }),
  [DISPLAY_TYPE.PRICE_ADJUSTMENT]: Object.freeze({
    // lineitemText: true, price: true, promotionId: true, campaignId: true, couponId: true, reasonCode: true, isAdjustment: true
  })
})

const validationRules = (v) => {
  const shipment = v.orderDetailOrigin.shipments.find(shipment => shipment.id === v.target.shipment.id)
  const pointOrigin = shipment.items.find(item => item.id === v.target.id).point
  const consumedPointOrigin = shipment.items.find(item => item.id === v.target.id).consumedPoint
  // 獲得ポイントチェック
  v.$validator.extend('pointCheck', {
    getMessage: (name, [ limit ]) => `獲得ポイントを${limit === '1' ? '減算' : '加算'}する事はできません。`,
    validate: (data, [ limit ]) => {
      if (limit === '1' && data < pointOrigin) {
        return false
      } else if (limit === '2' && data > pointOrigin) {
        return false
      }
      return true
    }
  })
  // 消費ポイントチェック
  v.$validator.extend('consumedPointCheck', {
    getMessage: (name, [ limit ]) => `消費ポイントを${limit === '1' ? '減算' : '加算'}する事はできません。`,
    validate: (data, [ limit ]) => {
      if (limit === '1' && data < consumedPointOrigin) {
        return false
      } else if (limit === '2' && data > consumedPointOrigin) {
        return false
      }
      return true
    }
  })
}

export default {
  name: 'OrderItem',
  components: {
    BaseTableDetail,
    BaseDialog,
    AnbunDialog,
    SearchSkus
  },
  mixins: [ orderUtil ],
  inject: ['parentValidator'],
  props: {
    mode: {
      type: Number,
      default: define.ORDER_MODE.NORMAL
    },
    displayType: {
      type: String,
      default: '',
      validator: function (value) {
        return _.values(DISPLAY_TYPE).indexOf(value) !== -1
      }
    },
    target: {
      type: Object,
      default: null
    },
    parent: { // targetが調整金額の場合、調整金額が紐づく注文明細
      type: Object,
      default: () => ({})
    },
    orderItems: {
      type: Object,
      default: () => ({})
    },
    orderDetail: {
      type: Object,
      default: () => ({})
    },
    orderDetailOrigin: {
      type: Object,
      default: () => ({})
    },
    orderAdjustments: {
      type: Array,
      default: () => []
    },
    validateScope: {
      type: String,
      default: 'orderItem'
    },
    readOnly: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      searchSkusDialogVisible: false,
      anbunDialogVisible: false,
      existMasterData: true
    }
  },
  computed: {
    ...mapGetters({
      orderPointConfigPoint: 'orderPointConfigPoint',
      orderPointConfigConsumedPoint: 'orderPointConfigConsumedPoint',
      siteTaxation: 'siteTaxation',
      shippingCharge: 'shippingCharge',
      codFee: 'codFee',
      cvsFee: 'cvsFee',
      getPointAdjustmentEditable: 'getPointAdjustmentEditable',
      getAnbunSelectTarget: 'getAnbunSelectTarget'
    }),
    ...mapGetters('OrderDetail', {
      siteCode: 'siteCode',
      orderDatetime: 'orderDatetime',
      taxInfo: 'taxInfo'
    }),
    ...mapGetters({
      getServiceSiteConfig: 'getServiceSiteConfig'
    }),
    basicTaxRate () {
      return math.divide([_.get(this.taxInfo, 'rate', 0), 100])
    },
    tableSetList () {
      switch (this.displayType) {
        case DISPLAY_TYPE.ITEM:
          return this.itemTable
        case DISPLAY_TYPE.GIFT_ITEM:
        case DISPLAY_TYPE.SHIPPING_ITEM:
          return this.otherItemTable
        case DISPLAY_TYPE.ADJUSTMENT_ITEM:
          return this.adjustmentItemTable
        case DISPLAY_TYPE.ITEM_PRICE_ADJUSTMENT:
        case DISPLAY_TYPE.GIFT_ITEM_PRICE_ADJUSTMENT:
        case DISPLAY_TYPE.SHIPPING_ITEM_PRICE_ADJUSTMENT:
        case DISPLAY_TYPE.PRICE_ADJUSTMENT:
          return this.priceAdjustmentTable
        case DISPLAY_TYPE.COMPONENT:
          return this.componentTable
        default:
          return []
      }
    },
    itemTable () {
      return [
        {
          label: 'ブランド', properties: [
            {
              path: 'brand', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'brand', editableCondition: this.editableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.BRAND
            }
          ]
        }, {
          label: 'SKU', properties: [
            {
              path: 'productId', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'productId', editableCondition: (row, path) => this.editableCondition(row, path) && (row.bundleItems || []).length === 0,
              validateRule: 'required', validateAs: 'SKU',
              blur: this.onBlurProductId,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.SKU
            }
          ]
        }, {
          label: '', properties: [
            {
              path: 'searchSkus', type: 'String', elementType: ELEMENT_TYPE.BUTTON,
              label: '商品検索', click: this.onSkuSearchClick, disabled: (row, path) => this.disabledSearchSkus(row, path) && (row.bundleItems || []).length === 0,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.PRODUCT_SEARCHER
            }
          ]
        }, {
          label: '商品名', properties: [
            {
              path: 'lineitemText', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'lineitemText', editableCondition: this.editableCondition,
              validateRule: 'required', validateAs: '商品名',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.PRODUCT_NAME
            }
          ]
        }, {
          label: 'カラー', properties: [
            {
              path: 'colorName', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'colorName', editableCondition: this.editableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.COLOR
            }
          ]
        }, {
          label: 'サイズ', properties: [
            {
              path: 'sizeName', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'sizeName', editableCondition: this.editableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.SIZE
            }
          ]
        }, {
          label: 'セール', properties: [
            {
              path: 'isSale', type: 'Boolean', elementType: ELEMENT_TYPE.CHECK_BOX,
              editableId: 'isSale', editableCondition: this.editableCondition,
              single: true, label: 'セール',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.IS_SALE
            }
          ]
        }, {
          label: 'ギフト', properties: [
            {
              path: 'isGift', type: 'Boolean', elementType: ELEMENT_TYPE.CHECK_BOX,
              editableId: 'isGift', editableCondition: this.editableCondition,
              single: true, label: 'ギフト',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.IS_GIFT
            }
          ]
        }, {
          label: '売価(税込)', properties: [
            {
              path: 'basePrice.gross', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER,
              editableId: 'basePrice', editableCondition: this.editableCondition,
              validateRule: 'required|min_value:0', validateAs: '売価(税込)',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.TAX_IN_BASE_PRICE
            }
          ]
        }, {
          label: '定価', properties: [
            {
              path: 'retailPrice', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER,
              editableId: 'retailPrice', editableCondition: this.editableCondition,
              validateRule: 'required|min_value:0', validateAs: '定価',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.BASE_PRICE
            }
          ]
        }, {
          label: '数量', properties: [
            { path: 'qty.current', type: 'Number', precision: 0, fractionDigits: 0,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.QUANTITY }
          ]
        }, {
          label: '税率( / 100)', properties: [
            {
              path: 'taxRate', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER, precision: 2, fractionDigits: 2,
              editableId: 'taxRate', editableCondition: this.editableCondition,
              validateRule: 'required|min_value:0', validateAs: '税率',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.TAX
            }
          ]
        }, {
          label: '金額(税込)', properties: [
            { path: 'price.gross', type: 'Number', precision: 0, fractionDigits: 0,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.GROSS_PRICE }
          ]
        }, {
          label: '獲得ポイント', properties: [
            { path: 'point', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'point',
              validateRule: this.needPointValidationRules ? `required|min_value:0|pointCheck:${this.orderPointConfigPoint || 0}` : 'required|min_value:0',
              validateAs: '獲得ポイント',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.POINT }
          ]
        }, {
          label: '消費ポイント', properties: [
            { path: 'consumedPoint', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'point',
              validateRule: this.needPointValidationRules ? `required|min_value:0|consumedPointCheck:${this.orderPointConfigConsumedPoint || 0}` : 'required|min_value:0',
              validateAs: '消費ポイント',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.CONSUMED_POINT }
          ]
        }, {
          label: '出荷指示送信開始日', properties: [
            { path: 'preparingShipmentStartedAt', type: 'Date', elementType: ELEMENT_TYPE.DATE_TIME,
              editableId: 'preparingShipmentStartedAt', editableCondition: this.editableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.PREPARING_SHIPMENT_STARTED_AT }
          ]
        }, {
          label: '発売日時', properties: [
            { path: 'releaseDatetime', type: 'Date',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.RELEASE_DATETIME }
          ]
        }, {
          label: '出荷有無', properties: [
            { path: 'isShipment', type: 'String', codeName: 'd_order_item.is_shipment',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.IS_SHIPMENT }
          ]
        }
      ]
    },
    componentTable () {
      return [
        {
          label: 'ブランド', properties: [
            {
              path: 'brand', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'brand', editableCondition: this.editableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.BRAND
            }
          ]
        }, {
          label: 'SKU', properties: [
            {
              path: 'productId', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'productId', editableCondition: this.editableCondition,
              validateRule: 'required', validateAs: 'SKU',
              blur: this.onBlurProductId,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.SKU
            }
          ]
        }, {
          label: '', properties: [
            {
              path: 'searchSkus', type: 'String', elementType: ELEMENT_TYPE.BUTTON,
              label: '商品検索', click: this.onSkuSearchClick, disabled: this.disabledSearchSkus,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.PRODUCT_SEARCHER
            }
          ]
        }, {
          label: '商品名', properties: [
            {
              path: 'lineitemText', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'lineitemText', editableCondition: this.editableCondition,
              validateRule: 'required', validateAs: '商品名',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.PRODUCT_NAME
            }
          ]
        }, {
          label: '数量', properties: [
            { path: 'qty.current', type: 'Number', precision: 0, fractionDigits: 0,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.QUANTITY }
          ]
        }, {
          label: '出荷指示送信開始日', properties: [
            { path: 'preparingShipmentStartedAt', type: 'Date', elementType: ELEMENT_TYPE.DATE_TIME,
              editableId: 'preparingShipmentStartedAt', editableCondition: this.editableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.PREPARING_SHIPMENT_STARTED_AT }
          ]
        }, {
          label: '発売日時', properties: [
            { path: 'releaseDatetime', type: 'Date',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.RELEASE_DATETIME }
          ]
        }, {
          label: '出荷有無', properties: [
            { path: 'isShipment', type: 'String', codeName: 'd_order_item.is_shipment',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ProductDetail.IS_SHIPMENT }
          ]
        }
      ]
    },
    otherItemTable () {
      return [
        {
          label: '表示名称', properties: [
            {
              path: 'lineitemText', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'lineitemText', editableCondition: this.editableCondition,
              validateRule: 'required', validateAs: '表示名称',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FeeDetail.LINE_ITEM_TEXT
            }
          ]
        }, {
          label: '売価(税込)', properties: [
            {
              path: 'basePrice.gross', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER,
              editableId: 'price', validateRule: 'required|min_value:0', validateAs: '売価(税込)'
            }
          ]
        }, {
          label: '金額(税込)', properties: [
            { path: 'price.gross', type: 'Number', precision: 0, fractionDigits: 0,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FeeDetail.GROSS_PRICE }
          ]
        }
      ]
    },
    adjustmentItemTable () {
      return [
        {
          label: '表示名称', properties: [
            {
              path: 'lineitemText', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'lineitemText', editableCondition: this.editableCondition,
              validateRule: 'required', validateAs: '表示名称',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FeeDetail.LINE_ITEM_TEXT
            }
          ]
        }, {
          label: '売価(税込)', properties: [
            {
              path: 'basePrice.gross', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER,
              editableId: 'price', validateRule: 'required', validateAs: '売価(税込)'
            }
          ]
        }, {
          label: '金額(税込)', properties: [
            { path: 'price.gross', type: 'Number', precision: 0, fractionDigits: 0,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FeeDetail.GROSS_PRICE }
          ]
        }
      ]
    },
    priceAdjustmentTable () {
      return [
        {
          label: '表示名称', properties: [
            {
              path: 'lineitemText', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'lineitemText', validateRule: 'required', validateAs: '表示名称',
              editableCondition: this.editablePointCondition
            }
          ]
        }, {
          label: 'プロモーションID', properties: [
            {
              path: 'promotionId', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'promotionId', editableCondition: this.editablePointCondition,
              funcId: this.displayType === DISPLAY_TYPE.PRICE_ADJUSTMENT
                ? define.PermissionKeys.Order.Detail.OrderInfo.TotalDetail.AdjustmentsDetail.PROMOTION_ID
                : define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.DiscountDetail.PROMOTION_ID
            }
          ]
        }, {
          label: 'キャンペーンID', properties: [
            {
              path: 'campaignId', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'campaignId', editableCondition: this.editablePointCondition,
              funcId: this.displayType === DISPLAY_TYPE.PRICE_ADJUSTMENT
                ? define.PermissionKeys.Order.Detail.OrderInfo.TotalDetail.AdjustmentsDetail.CAMPAIGN_ID
                : define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.DiscountDetail.CAMPAIGN_ID
            }
          ]
        }, {
          label: 'クーポンID', properties: [
            {
              path: 'couponId', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'couponId', editableCondition: this.editablePointCondition,
              funcId: this.displayType === DISPLAY_TYPE.PRICE_ADJUSTMENT
                ? define.PermissionKeys.Order.Detail.OrderInfo.TotalDetail.AdjustmentsDetail.COUPON_ID
                : define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.DiscountDetail.COUPON_ID
            }
          ]
        }, {
          label: '理由コード', properties: [
            {
              path: 'reasonCode', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'reasonCode',
              funcId: this.displayType === DISPLAY_TYPE.PRICE_ADJUSTMENT
                ? define.PermissionKeys.Order.Detail.OrderInfo.TotalDetail.AdjustmentsDetail.REASON_CODE
                : define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.DiscountDetail.REASON_CODE
            }
          ]
        }, {
          label: '単価(税込)', properties: [
            {
              path: 'basePrice.gross', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER,
              editableId: 'price', validateRule: 'required', validateAs: '単価(税込)'
            }
          ]
        }, {
          label: '数量',
          isShow: this.displayType === DISPLAY_TYPE.ITEM_PRICE_ADJUSTMENT &&
            this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.DiscountDetail.QUANTITY),
          properties: [
            {
              path: () => _.get(this.parent, 'qty.current', 0),
              type: 'Number', elementType: ELEMENT_TYPE.FUNCTION
            }
          ]
        }, {
          label: '金額(税込)', properties: [
            { path: 'price.gross', type: 'Number', precision: 0, fractionDigits: 0,
              funcId: this.displayType === DISPLAY_TYPE.PRICE_ADJUSTMENT
                ? define.PermissionKeys.Order.Detail.OrderInfo.TotalDetail.AdjustmentsDetail.GROSS_PRICE
                : define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.DiscountDetail.GROSS_PRICE }
          ]
        }
      ]
    },
    editable () {
      return this.readOnly || [define.ORDER_MODE.NORMAL, define.ORDER_MODE.CAPTURE, define.ORDER_MODE.CHANGE_PAYMENT].includes(this.mode)
        ? {} : EDITABLE[this.displayType]
    },
    parentTaxRate () {
      return _.get(this.parent, 'taxRate') || this.basicTaxRate
    },
    needPointValidationRules () {
      // カスタムバリデーションルール条件(新規行/構成品であれば不要)
      return this.displayType === 'item' && this.target.id != null
    },
    shipments () {
      return this.orderDetail.shipments
    },
    showAnbunButton () {
      return (this.displayType === DISPLAY_TYPE.PRICE_ADJUSTMENT &&
                this.getAnbunSelectTarget(this.siteCode) === '1')
    }
  },
  watch: {
    'target.lineitemText' (val) {
      if (!this.existMasterData) {
        // 商品マスタ利用しない場合、商品名と表示名を同期する
        this.target.productName = val
      }
    },
    'target.qty.current' (val) {
      switch (this.displayType) {
        case DISPLAY_TYPE.ITEM:
          this.calculate()
          break
      }
    },
    'target.basePrice.gross' (val) {
      this.calculate()
    }
  },
  async created () {
    if (this.needPointValidationRules) {
      validationRules(this)
    }
  },
  async updated () {
    await this.$validator.validate(`${this.validateScope}.*`)
  },
  methods: {

    /** 編集可否条件 */
    editableCondition: function (row, path) {

      const canEdit = this.$_orderUtil_canEditOrderItem(
        this.target, this.displayType === DISPLAY_TYPE.ITEM, this.mode, this.orderDetailOrigin)
      if (!canEdit) {
        return false
      }

      if (this.displayType === DISPLAY_TYPE.ITEM &&
          path === 'isGift') {
        // 商品明細のギフトフラグ
        return _.get(this.orderDetail, 'giftType') === define.GIFT_TYPE.INDIVIDUALLY
      }

      if (this.displayType === DISPLAY_TYPE.SHIPPING_ITEM &&
          path === 'lineitemText') {
        // 送料明細の表示名称
        const codFeeConfig = this.codFee(this.siteCode)
        if (row.productId === codFeeConfig.productId) {
          return codFeeConfig.editable === '1'
        }
        const cvsFeeConfig = this.cvsFee(this.siteCode)
        if (row.productId === cvsFeeConfig.productId) {
          return cvsFeeConfig.editable === '1'
        }
        return this.shippingCharge(this.siteCode).editable === '1'
      }

      return true
    },
    editablePointCondition (row, path) {
      if (row.isPoint !== 1) {
        return true
      }
      let editable = false
      switch (path) {
        case 'promotionId':
          editable = this.getPointAdjustmentEditable(this.siteCode, 'promotion_id') === '1'
          break
        case 'campaignId':
          editable = this.getPointAdjustmentEditable(this.siteCode, 'campaign_id') === '1'
          break
        case 'couponId':
          editable = this.getPointAdjustmentEditable(this.siteCode, 'coupon_id') === '1'
          break
        case 'lineitemText':
          editable = this.getPointAdjustmentEditable(this.siteCode, 'lineitem_text') === '1'
          break
        default:
      }
      return editable
    },
    calculate () {
      switch (this.displayType) {
        case DISPLAY_TYPE.ITEM:
          this.target.price.gross = math.multiply(
            [this.target.basePrice.gross, this.target.qty.current]
          )
          break
        case DISPLAY_TYPE.GIFT_ITEM:
        case DISPLAY_TYPE.SHIPPING_ITEM:
        case DISPLAY_TYPE.GIFT_ITEM_PRICE_ADJUSTMENT:
        case DISPLAY_TYPE.SHIPPING_ITEM_PRICE_ADJUSTMENT:
        case DISPLAY_TYPE.PRICE_ADJUSTMENT:
          this.target.price.gross = this.target.basePrice.gross
          break
        case DISPLAY_TYPE.ADJUSTMENT_ITEM:
          // 注文調整金額の更新処理
          const diff = -(this.target.price.gross - this.target.basePrice.gross)
          this.calculateOrderAdjustments(diff)
          // 注文調整金額按分明細の更新
          this.target.price.gross = this.target.basePrice.gross
          break
        case DISPLAY_TYPE.ITEM_PRICE_ADJUSTMENT:
          this.target.price.gross = math.multiply(
            [this.target.basePrice.gross, _.get(this.parent, 'qty.current', 0)]
          )
          break
      }
    },
    calculateOrderAdjustments (diff) {
      // 注文明細の属性情報から紐づく注文調整金額.IDを取得する
      let orderAdjustmentId = _.chain(this.target.attributes)
        .filter((val) => val.attrId === 'orderAdjustmentId')
        .map((val) => val.attrValue)
        .value()[0]
      orderAdjustmentId = Number(orderAdjustmentId)

      // 取得した注文調整金額.IDのレコードの注文調整金額を更新する
      let isExist = false
      this.orderAdjustments.forEach((val) => {
        if (val.id === orderAdjustmentId) {
          isExist = true
          val.basePrice.gross = Number(val.basePrice.gross) + diff
          val.price.gross = Number(val.price.gross) + diff
        }
      })

      // 取得した注文調整金額.IDのレコードが存在しない場合は
      // 更新履歴(初回)から注文調整金額.調整種別、注文調整金額.表示順を取得する
      if (!isExist) {
        const history = _.chain(this.orderDetailOrigin)
          .get('histories')
          .value()
          .slice(-1)[0]

        const linkInfo = _.chain(history)
          .get('merchandisePriceAdjustments')
          .filter((val) => val.id === orderAdjustmentId)
          .map((val) => ({ 'adjustType': val.adjustType, 'position': val.position }))
          .value()[0]

        this.orderAdjustments.forEach((val) => {
          if (val.adjustType === linkInfo.adjustType && val.position === linkInfo.position) {
            val.basePrice.gross = Number(val.basePrice.gross) + diff
            val.price.gross = Number(val.price.gross) + diff
          }
        })
      }
    },
    async onBlurProductId (event, val, path, before, after) {
      if (before !== after) {
        // SKUが変更されている場合
        let skuData
        try {
          this.$loadingService.show({ type: 'light' })
          let query = orderQuery.GRAPH_QL_QUERY_SKU_LIST
          query = common.addGraphQlCondition(query, { skuCode: this.target.productId, isActive: 1 })
          query = common.addGraphQlCondition(query, { siteCode: this.siteCode, baseDateTime: this.orderDatetime })
          query = common.addGraphQlCondition(query, { siteCode: this.siteCode, baseDateTime: this.orderDatetime })
          const res = await common.postGraphQl(query)
          skuData = _.get(res, 'data.data.skus.edges[0].node')
        } finally {
          if (skuData) {
            this.skuSelected(skuData)
          } else {
            this.$alert(
              '商品情報が見つかりませんでした。',
              define.MESSAGE_TITLE,
              { type: 'warning', showClose: false }
            ).then(() => {
              this.target.productId = before
            })
          }
          this.$loadingService.close()
        }
      }
    },
    merchandiseQtyTotal (row) {
      let qty = _.chain(this.orderItems)
        .values()
        .flatten()
        .reduce((acc, cur) => {
          acc.push(cur.id === this.target.id ? this.target : cur)
          return acc
        }, [])
        .map('qty.current')
        .value()
      return math.add(qty, config.ORDER_CONFIG.quantity.roundType, config.ORDER_CONFIG.quantity.decimalDigit)
    },
    disabledSearchSkus (row) {
      return this.editable['productId'] && this.editableCondition(row, 'productId') &&
      // 売上が計上されている場合、SKUの編集不可
      !(row.salesItems || []).some((salesItem) => salesItem.salesDatetime != null && Number(_.get(salesItem, 'adjustedPrice.gross', 0)) > 0)
    },
    showOnlyCloseButton () {
      return !this.editable['isAdjustment']
    },
    onSkuSearchClick (row) {
      this.searchSkusDialogVisible = true
    },
    onAnbunSelectClick (row) {
      this.anbunDialogVisible = true
    },

    /**
     * 明細に商品情報を設定
     * @param {*} row 商品情報
     */
    skuSelected (row) {

      // 商品マスタ利用フラグ
      // TODO:  サービスサイト設定
      //        SKU変更時、商品検索ダイアログの表示なども同じ設定を参照する
      this.existMasterData = true

      // 属性置換をセット
      this.$set(this.target, 'attributeReplace', 1)

      this.target.lineitemText = _.get(row, 'product.name') // 表示名
      this.target.productId = _.get(row, 'code')  // 商品ID
      this.target.brand = _.get(row, 'product.brand.name')  // ブランド名
      this.target.productName = _.get(row, 'product.name')  // 商品名

      // バンドル構成品ではない場合
      if (this.displayType !== 'component') {

        /** 売価、定価 */
        const plu = _.chain(row)
          .get('plus')
          .sortBy('saleType')
          .reverse()
          .first()
          .value()
        const regularPlu = _.chain(row)
          .get('plus')
          .find(val => val.saleType === define.SALE_TYPE.REGULAR_PRICE)
          .value()

        if (this.siteTaxation(this.siteCode) === 0) {
        // 税抜表記
          this.target.basePrice.net = _.chain(plu)
            .get('priceExTax', 0)
            .thru(val => Number(val))
            .value()
          this.target.retailPrice = Number(_.get(regularPlu, 'priceExTax', 0))
        } else {
        // 税込表記
          this.target.basePrice.gross = _.chain(plu)
            .get('priceInTax', 0)
            .thru(val => Number(val))
            .value()
          this.target.retailPrice = Number(_.get(regularPlu, 'priceInTax', 0))
        }

        // 税率
        this.target.taxRate =
          math.divide([_.get(row, 'product.taxes[0].rate'), 100], null, 2) ||
          this.basicTaxRate
        // セール区分
        this.target.isSale = _.chain(row)
          .get('plus')
          .find(val => val.saleType === define.SALE_TYPE.SALE)
          .value() == null ? 0 : 1
        this.target.sku = {
          product: _.get(row, 'product.taxes[0]') ||
            { taxes: [ _.cloneDeep(this.taxInfo) ] }
        }
      }

      this.target.colorName = _.get(row, 'color.name')  // カラー名
      this.target.sizeName = _.get(row, 'size.name')  // サイズ名
      this.preparingShipmentStartedAt = _.get(row, 'preparingShipmentStartedAt')  // 出荷指示送信開始日
      this.releaseDatetime = _.get(row, 'releaseDatetime')  // 発売日
      this.isShipment = _.get(row, 'isShipment', true)  // 出荷有無

      // 属性情報セット
      this.$_orderUtil_setOrderItemAttributes(this.displayType, this.target, row)

      this.searchSkusDialogVisible = false

    }
  }
}
</script>
